import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/shared/auth/auth-provider';
import { LoadingPanel } from '@/shared/ui/spinner';

/**
 * Gate for signed-in routes.
 *
 * While the stored token is being validated the app shows a loader rather
 * than bouncing to the public page, otherwise a refresh would flash the
 * landing page before landing back on the page the user was on.
 *
 * A visitor without a session lands on `/`, the landing page — not on
 * `/login`. Sign-in is a dialog over that page, so `/` is where the public
 * half of the app lives; the sign-out path leads here too, which is why
 * leaving the workspace returns to the home screen rather than to a URL that
 * says "login" over a page that is the landing page anyway. `/login` still
 * works for a link that names it.
 *
 * The requested screen travels with the redirect, so signing in from a deep
 * link resumes there instead of at the dashboard.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <LoadingPanel />
      </div>
    );
  }

  if (status === 'anonymous') {
    /*
     * `/` is public, so it is only the screens below it that carry a
     * destination: a guard reached *at* `/` would mean the layout route had
     * swallowed the landing page, and there would be nothing to come back to.
     */
    const from = location.pathname + location.search;

    return <Navigate to="/" state={from === '/' ? undefined : { from }} replace />;
  }

  return <>{children}</>;
}
